import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Eye, Copy, ExternalLink, Share2, Download,
  RefreshCw, CreditCard, Briefcase, Building, Mail,
  Phone, Hash, Smartphone, Linkedin, Info,
  Settings,
} from 'lucide-react';
import { useNameCard } from '../../hooks/useNameCard';
import { nameCardAPI } from '../../services/api';
import { StatusBadge } from '../UI/StatusBadge';
import ConfirmModal from '../UI/ConfirmModal';
import toast from '../../utils/toast';

const FIELD_CONFIG = [
  { key: 'designation', label: 'Designation', icon: Briefcase, valueKey: 'staff_designation' },
  { key: 'department', label: 'Department', icon: Building, valueKey: 'staff_department' },
  { key: 'work_email', label: 'Work Email', icon: Mail, valueKey: null },
  { key: 'work_phone', label: 'Work Phone', icon: Phone, valueKey: null },
  { key: 'extension', label: 'Direct line', icon: Hash, valueKey: null },
  { key: 'personal_phone', label: 'Personal Mobile', icon: Smartphone, valueKey: null },
  { key: 'linkedin', label: 'LinkedIn', icon: Linkedin, valueKey: null },
];

/**
 * Name Card management tab for the Profile page.
 * Only visible when consent is granted (hasCard=true).
 */
const NameCardTab = ({ profile, hrProfile }) => {
  const {
    nameCard, isLoading,
    updateNameCard, regenerateLink,
    isUpdating, isRegenerating,
  } = useNameCard();

  const [linkedinInput, setLinkedinInput] = useState('');
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [qrUrl, setQrUrl] = useState(null);

  useEffect(() => {
    if (nameCard?.linkedin_url) {
      setLinkedinInput(nameCard.linkedin_url);
    }
  }, [nameCard?.linkedin_url]);

  const loadQR = useCallback(async () => {
    try {
      const response = await nameCardAPI.getQRCode();
      const url = URL.createObjectURL(response.data);
      setQrUrl(url);
    } catch {
      // QR will just not show
    }
  }, []);

  useEffect(() => {
    if (nameCard?.is_active) {
      loadQR();
    }
    return () => {
      if (qrUrl) URL.revokeObjectURL(qrUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameCard?.is_active, nameCard?.id, loadQR]);

  const toggleVisibility = useCallback((field) => {
    if (!nameCard) return;
    const key = `show_${field}`;
    updateNameCard({ [key]: !nameCard[key] });
  }, [nameCard, updateNameCard]);

  const toggleActive = useCallback(() => {
    if (!nameCard) return;
    updateNameCard({ is_active: !nameCard.is_active });
  }, [nameCard, updateNameCard]);

  const handleLinkedinSave = useCallback(() => {
    if (!nameCard) return;
    const trimmed = linkedinInput.trim();
    if (trimmed === nameCard.linkedin_url) return;
    updateNameCard({ linkedin_url: trimmed });
    toast.success('LinkedIn URL updated');
  }, [nameCard, linkedinInput, updateNameCard]);

  const handleCopyLink = useCallback(async () => {
    if (!nameCard?.public_url) return;
    try {
      await navigator.clipboard.writeText(nameCard.public_url);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Failed to copy link');
    }
  }, [nameCard?.public_url]);

  const handleShare = useCallback(async () => {
    if (!nameCard?.public_url || !navigator.share) return;
    try {
      await navigator.share({
        title: `${nameCard.staff_name} - Digital Name Card`,
        text: `Contact card for ${nameCard.staff_name}`,
        url: nameCard.public_url,
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error('Sharing failed');
      }
    }
  }, [nameCard]);

  const handleDownloadQR = useCallback(() => {
    if (!qrUrl) return;
    const a = document.createElement('a');
    a.href = qrUrl;
    a.download = `name-card-qr-${profile?.staff_id || 'card'}.png`;
    a.click();
  }, [qrUrl, profile?.staff_id]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
          <div className="h-4 bg-gray-200 rounded w-2/3" />
        </div>
      </div>
    );
  }

  if (!nameCard) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-12 px-6">
          <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Name Card</h3>
          <p className="text-sm text-gray-500">
            Activate your digital name card from the Legal & Compliance tab first.
          </p>
          <Link to="/profile?tab=legal" className="btn btn-primary mt-4">
            Go to Legal & Compliance
            <ExternalLink className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const isBusy = isUpdating || isRegenerating;

  return (
    <div className="space-y-6">
      {/* Card Status */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <CreditCard className="w-5 h-5 text-blue-800 flex-shrink-0" />
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Card Status</h3>
          </div>
          <button
            onClick={toggleActive}
            disabled={isBusy}
            role="switch"
            aria-checked={nameCard.is_active}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 before:absolute before:-inset-[10px] before:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
              nameCard.is_active ? 'bg-blue-800' : 'bg-gray-300'
            } ${isBusy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                nameCard.is_active ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        <div className="flex items-center gap-2 mb-2">
          <StatusBadge
            status={nameCard.is_active ? 'active' : 'on_hold'}
            label={nameCard.is_active ? 'Active' : 'Paused'}
            showIcon
          />
          {!nameCard.is_active && (
            <span className="text-xs text-amber-600">Visitors see "card unavailable"</span>
          )}
        </div>

        {nameCard.is_active && nameCard.public_url && (
          <div className="flex items-center gap-2 mt-3 p-2 bg-gray-50 rounded-lg">
            <code className="text-xs text-gray-600 flex-1 truncate">{nameCard.public_url}</code>
            <button
              onClick={handleCopyLink}
              className="icon-button"
              title="Copy link"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Visible Information */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <Eye className="w-5 h-5 text-blue-800" />
          <h3 className="text-lg font-semibold text-gray-900">Visible Information</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">Only fields you authorized in Legal & Compliance</p>

        <div className="divide-y divide-gray-100">
          {/* Full Name (always on) */}
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">Full Name</span>
              <span className="field-display">{nameCard.staff_name}</span>
            </div>
            <span className="text-xs text-gray-400 italic">always on</span>
          </div>

          {/* Togglable fields */}
          {FIELD_CONFIG.filter(f => nameCard[`consent_${f.key}`]).map(({ key, label, icon: Icon }) => (
            <div key={key} className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <Icon className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">{label}</span>
              </div>
              <button
                onClick={() => toggleVisibility(key)}
                disabled={isBusy}
                role="switch"
                aria-checked={nameCard[`show_${key}`]}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 before:absolute before:-inset-[10px] before:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                  nameCard[`show_${key}`] ? 'bg-blue-800' : 'bg-gray-300'
                } ${isBusy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                    nameCard[`show_${key}`] ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-3 p-3 bg-blue-50 rounded-lg flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-700">
            To add or remove fields from this list, update your consent in the{' '}
            <Link to="/profile?tab=legal" className="font-medium underline">Legal & Compliance</Link> tab.
          </p>
        </div>
      </div>

      {/* LinkedIn URL */}
      {nameCard.consent_linkedin && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Linkedin className="w-5 h-5 text-blue-800" />
            <h3 className="text-lg font-semibold text-gray-900">LinkedIn Profile</h3>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="url"
              value={linkedinInput}
              onChange={(e) => setLinkedinInput(e.target.value)}
              onBlur={handleLinkedinSave}
              placeholder="https://linkedin.com/in/your-profile"
              className="input flex-1"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Leave blank to hide LinkedIn from your card.</p>
        </div>
      )}

      {/* Quick Share */}
      {nameCard.is_active && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Share2 className="w-5 h-5 text-blue-800" />
            <h3 className="text-lg font-semibold text-gray-900">Quick Share</h3>
          </div>

          <div className="flex flex-col sm:flex-row items-start gap-4">
            {qrUrl && (
              <div className="bg-white p-2 rounded-lg border border-gray-200 shadow-sm">
                <img src={qrUrl} alt="Name Card QR Code" className="w-32 h-32" />
              </div>
            )}
            <div className="flex flex-col gap-2">
              <button onClick={handleCopyLink} className="btn btn-outline self-start sm:self-auto">
                <Copy className="w-4 h-4" />
                Copy Link
              </button>
              {typeof navigator.share === 'function' && (
                <button onClick={handleShare} className="btn btn-outline self-start sm:self-auto">
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
              )}
              {qrUrl && (
                <button onClick={handleDownloadQR} className="btn btn-outline self-start sm:self-auto">
                  <Download className="w-4 h-4" />
                  Download QR Image
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Statistics */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Eye className="w-5 h-5 text-blue-800" />
          <h3 className="text-lg font-semibold text-gray-900">Statistics</h3>
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <span>{nameCard.view_count || 0} total views</span>
          <span className="text-gray-300">|</span>
          <span>Created {new Date(nameCard.created_at).toLocaleDateString('en-SG', {
            day: 'numeric', month: 'short', year: 'numeric'
          })}</span>
        </div>
      </div>

      {/* Advanced */}
      <div className="bg-gray-50 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Settings className="w-4 h-4 text-gray-500" />
          <h4 className="text-base font-semibold text-gray-900">Advanced</h4>
        </div>

        <button
          onClick={() => setShowRegenerateConfirm(true)}
          disabled={isBusy}
          className="btn border-amber-300 bg-white text-amber-700 hover:bg-amber-50 focus-visible:ring-amber-500"
        >
          <RefreshCw className={`w-4 h-4 ${isRegenerating ? 'animate-spin' : ''}`} />
          {isRegenerating ? 'Regenerating...' : 'Regenerate Link'}
        </button>
        <p className="text-xs text-gray-500 mt-2">
          Your old link will permanently stop working. Anyone who saved it will see "card unavailable."
        </p>

        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            To fully delete your card and revoke consent, turn off the consent toggle in the{' '}
            <Link to="/profile?tab=legal" className="text-blue-600 hover:text-blue-800 font-medium">
              Legal & Compliance
            </Link> tab.
          </p>
        </div>
      </div>

      {/* Regenerate Confirmation Modal */}
      {showRegenerateConfirm && (
        <ConfirmModal
          isOpen={showRegenerateConfirm}
          onCancel={() => setShowRegenerateConfirm(false)}
          onConfirm={() => {
            regenerateLink();
            setShowRegenerateConfirm(false);
          }}
          title="Regenerate Link?"
          message="This will create a new URL for your name card. Your old link will permanently stop working. Anyone who saved the old link will see 'card unavailable.' This action cannot be undone."
          confirmLabel="Regenerate"
          variant="danger"
        />
      )}
    </div>
  );
};

export default NameCardTab;
